export type ScreenshotAspectRatio =
  | '16:9'
  | '9:16'
  | '4:3'
  | '3:4'
  | '1:1'
  | '32:9'
  | '9:32'
  | 'custom';

export interface ScreenScreenshotSetV1 {
  enabled?: boolean;
  profileName?: string;
  targetFolder?: string;
  filePrefix?: string;
  format?: 'png' | 'jpeg' | 'webp';
  quality?: number;
  maxCount?: number;
  captureDelayMs?: number;
  width?: number;
  height?: number;
  aspectRatio?: ScreenshotAspectRatio;
  includeOverlay?: boolean;
  includeTimestamp?: boolean;
  includeTitleCard?: boolean;
}

/**
 * Bandwidth-saving controls applied by the player engine.
 *
 * All values are optional — an unset field falls back to engine defaults so an
 * older settings document keeps working. Hour values are interpreted in the
 * device's local time zone (matching the rest of the schedule subsystem).
 */
export interface ScreenBandwidthSettingsV1 {
  /**
   * How often the player should poll for bundle updates. Realtime updates
   * still apply on top; this only changes the polling cadence between
   * Realtime pings. Clamped to [1, 1440] minutes (1 minute … 24 hours) at
   * normalize time. Default = 5.
   */
  syncIntervalMinutes?: number;
  /**
   * "Quiet hours": a daily wall-clock window during which the player skips
   * caching newly-discovered assets. Bundle metadata + heartbeat traffic
   * still flows; only the bytes-heavy media downloads are paused. The
   * currently playing revision is unaffected.
   */
  quietHours?: {
    enabled?: boolean;
    /** 0–23 local hour. Inclusive start. */
    startHour?: number;
    /** 0–23 local hour. Exclusive end. End <= start wraps midnight. */
    endHour?: number;
  };
  /**
   * Optional "pre-download window": a daily wall-clock window during which
   * the player aggressively caches media (overrides quiet hours). Outside
   * this window, the player still caches lazily — the window just lets
   * admins bias downloads toward off-peak hours.
   */
  preDownload?: {
    enabled?: boolean;
    /** 0–23 local hour. Inclusive start. */
    startHour?: number;
    /** 0–23 local hour. Exclusive end. End <= start wraps midnight. */
    endHour?: number;
  };
  /**
   * Hard ceilings on the engine's outbound network. Sequential downloads
   * are the default; concurrency > 1 fans out fetches in parallel.
   */
  bandwidth?: {
    /** 1–8. Default 1 (sequential, current behavior). */
    maxConcurrentDownloads?: number;
    /** Bytes/sec ceiling per device. 0 / undefined = unlimited. */
    maxBytesPerSecond?: number;
  };
}

export interface ScreenSettingsV1 {
  screenshotSet?: ScreenScreenshotSetV1;
  /** Persisted only in v1; runtime playback behavior intentionally unchanged. */
  syncPlaybackEnabled?: boolean;
  /** Bandwidth-saving controls applied by the player engine. */
  bandwidth?: ScreenBandwidthSettingsV1;
}

const DEFAULT_SETTINGS: ScreenSettingsV1 = {};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function normalizeScreenSettings(input: unknown): ScreenSettingsV1 {
  const rec = asRecord(input);
  if (!rec) return { ...DEFAULT_SETTINGS };

  const screenshotRec = asRecord(rec.screenshotSet);
  const screenshotSet: ScreenScreenshotSetV1 | undefined = screenshotRec
    ? {
        enabled: asBoolean(screenshotRec.enabled),
        profileName: asString(screenshotRec.profileName),
        targetFolder: asString(screenshotRec.targetFolder),
        filePrefix: asString(screenshotRec.filePrefix),
        format:
          screenshotRec.format === 'png' ||
          screenshotRec.format === 'jpeg' ||
          screenshotRec.format === 'webp'
            ? screenshotRec.format
            : undefined,
        quality: asNumber(screenshotRec.quality),
        maxCount: asNumber(screenshotRec.maxCount),
        captureDelayMs: asNumber(screenshotRec.captureDelayMs),
        width: asNumber(screenshotRec.width),
        height: asNumber(screenshotRec.height),
        aspectRatio:
          screenshotRec.aspectRatio === '16:9' ||
          screenshotRec.aspectRatio === '9:16' ||
          screenshotRec.aspectRatio === '4:3' ||
          screenshotRec.aspectRatio === '3:4' ||
          screenshotRec.aspectRatio === '1:1' ||
          screenshotRec.aspectRatio === '32:9' ||
          screenshotRec.aspectRatio === '9:32' ||
          screenshotRec.aspectRatio === 'custom'
            ? screenshotRec.aspectRatio
            : undefined,
        includeOverlay: asBoolean(screenshotRec.includeOverlay),
        includeTimestamp: asBoolean(screenshotRec.includeTimestamp),
        includeTitleCard: asBoolean(screenshotRec.includeTitleCard),
      }
    : undefined;

  return {
    screenshotSet,
    syncPlaybackEnabled: asBoolean(rec.syncPlaybackEnabled),
    bandwidth: normalizeBandwidth(rec.bandwidth),
  };
}

function clampHour(value: unknown): number | undefined {
  const n = asNumber(value);
  if (n === undefined) return undefined;
  const intHour = Math.trunc(n);
  if (intHour < 0 || intHour > 23) return undefined;
  return intHour;
}

function normalizeWindow(rec: Record<string, unknown> | null) {
  if (!rec) return undefined;
  const enabled = asBoolean(rec.enabled);
  const startHour = clampHour(rec.startHour);
  const endHour = clampHour(rec.endHour);
  if (enabled === undefined && startHour === undefined && endHour === undefined) {
    return undefined;
  }
  return { enabled, startHour, endHour };
}

export function normalizeBandwidth(
  input: unknown,
): ScreenBandwidthSettingsV1 | undefined {
  const rec = asRecord(input);
  if (!rec) return undefined;

  let syncIntervalMinutes = asNumber(rec.syncIntervalMinutes);
  if (syncIntervalMinutes !== undefined) {
    syncIntervalMinutes = Math.max(1, Math.min(1440, Math.round(syncIntervalMinutes)));
  }

  const quietHours = normalizeWindow(asRecord(rec.quietHours));
  const preDownload = normalizeWindow(asRecord(rec.preDownload));

  const bandwidthRec = asRecord(rec.bandwidth);
  let bandwidth: ScreenBandwidthSettingsV1['bandwidth'] | undefined;
  if (bandwidthRec) {
    let maxConcurrent = asNumber(bandwidthRec.maxConcurrentDownloads);
    if (maxConcurrent !== undefined) {
      maxConcurrent = Math.max(1, Math.min(8, Math.round(maxConcurrent)));
    }
    let maxBps = asNumber(bandwidthRec.maxBytesPerSecond);
    if (maxBps !== undefined) {
      maxBps = Math.max(0, Math.round(maxBps));
    }
    if (maxConcurrent !== undefined || maxBps !== undefined) {
      bandwidth = {
        maxConcurrentDownloads: maxConcurrent,
        maxBytesPerSecond: maxBps,
      };
    }
  }

  if (
    syncIntervalMinutes === undefined &&
    !quietHours &&
    !preDownload &&
    !bandwidth
  ) {
    return undefined;
  }
  return {
    syncIntervalMinutes,
    quietHours,
    preDownload,
    bandwidth,
  };
}

export function normalizeScreenTags(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    if (typeof raw !== 'string') continue;
    const tag = raw.trim();
    if (!tag) continue;
    const key = tag.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}
